<template>
	<n-modal v-model:show="showModal" :mask-closable="!payLoading" :close-on-esc="false">
		<div
			:class="['pc-pay-card', { 'pc-pay-card--en': isEnglishLocale }]"
			role="dialog"
			aria-modal="true"
			v-show="areaPayment.length > 0"
		>
			<h3 class="pay-title">{{ t("user.pc.wallet.selectPayment") }}</h3>
			<!-- 地区列表 -->
			<ul class="tab">
				<li
					:class="[
						'tab-item',
						'pc-pay-card__area-btn',
						{
							active: item.name === currentArea,
							'pc-pay-card__area-btn--active': item.name === currentArea,
						},
					]"
					v-for="(item, index) in areaPayment"
					:key="index"
					type="button"
					@click="currentArea = item.name"
				>
					{{ displayPayAreaName(item.name, t) }}
				</li>
			</ul>
			<!-- 支付列表 -->
			<ul class="pay-list">
				<li v-for="(item, index) in payList" :key="index" @click="curPayId = index">
					<div :class="['pay-item', { active: index === curPayId }]">
						<van-image class="icon" :src="resolvePaymentIcon(item)" fit="cover" alt=""> </van-image>
						<p>
							{{ displayPayMethodName(item, t) }}
							<span class="coin">{{ isCoinMethod(item) ? `(${coin})` : "" }}</span>
						</p>
					</div>
				</li>
			</ul>
			<!-- 支付按鈕 -->
			<div :class="['btn-pay', { gray: !isEnoughCoin && isCoinPay }]">
				<PcPayAgree v-model="isAgree" />
				<n-button
					:loading="payLoading"
					type="primary"
					color="#111111"
					text-color="#ffffff"
					class="pc-pay-card__submit"
					@click="handlePay"
				>
					{{ btnText }}
				</n-button>
			</div>
		</div>
	</n-modal>
</template>

<script setup>
import { ref, watchEffect, computed, onMounted } from "vue";
import { watch } from "vue";
import { getUserCoin, coinPay, checkPay } from "~/api/user";
import usePixel from "~/hooks/usePixel";
import { useI18n } from "~/composables/useI18n";
import { resolvePaymentIcon } from "@variants/seer/user/pc/components/MyWallet/payIcons";
import { displayPayAreaName, displayPayMethodName } from "~/components/pay/payment-i18n";
import PcPayAgree from "~/components/pay/pc-pay-agree.vue";
const { reportGooglePixelEvent, reportPurchase } = usePixel();
const { t, locale } = useI18n();
const isEnglishLocale = computed(() => locale.value === "en");

const props = defineProps({
	finalPrice: {
		type: [String, Number],
		default: 0,
	},
	orderInfo: {
		type: Object,
		default: () => ({}),
	},
	close: {
		type: Function,
		default: () => {},
	},
	areaPayment: {
		type: Array,
		default: () => [],
	},
	onSuccess: {
		type: Function,
		default: () => {},
	},
});

// 初始化 naive-ui API
let message = null;
let modal = null;
let isApiInitialized = false;

const initNaiveApi = async () => {
	if (isApiInitialized || !import.meta.client) return;

	try {
		const { createDiscreteApi } = await import("naive-ui/es/discrete");
		const api = createDiscreteApi(["message", "modal"]);
		message = api.message;
		modal = api.modal;
		isApiInitialized = true;
	} catch (error) {
		console.error("Failed to initialize naive-ui API:", error);
	}
};

// 在组件挂载时初始化
onMounted(() => {
	initNaiveApi();
});

const showModal = ref(true); //支付彈窗
watchEffect(() => {
	if (!showModal.value) {
		props.close();
	}
});

const closePayModal = () => {
	if (payLoading.value) return;
	showModal.value = false;
};

const displayAmount = computed(() => {
	const amount = Number(props.finalPrice);
	if (Number.isNaN(amount)) return "...";
	return amount.toFixed(2);
});

const currentArea = ref();
watch(
	() => props.areaPayment,
	() => {
		currentArea.value = props.areaPayment[0]?.name;
	},
	{
		immediate: true,
	}
);
const payList = computed(
	() => props.areaPayment.find(v => v.name === currentArea.value)?.list || []
);
const curPayId = ref(0); // 當前選擇的支付方式索引
watch(
	() => currentArea.value,
	() => {
		curPayId.value = 0;
	},
	{
		immediate: true,
	}
);
watch(payList, list => {
	if (curPayId.value >= list.length) {
		curPayId.value = 0;
	}
});
const selectedPayMethod = computed(() => payList.value[curPayId.value] || null);
const isCoinMethod = item => /coin|點數|点数/i.test(item?.name || "") || item?.type === "coin";
const isCoinPay = computed(() => isCoinMethod(selectedPayMethod.value));

const isAgree = ref(true);

// 支付處理
const handlePay = async () => {
	if (!isAgree.value) {
		message.warning(t("user.pc.wallet.agreementRequired"));
		return;
	}
	reportGooglePixelEvent("gw_pay_event");

	const { third_order_id } = props.orderInfo;
	const urlList = props.orderInfo.area_payment.find(v => v.name === currentArea.value).list;
	const payUrl = urlList.find(v => v.name === selectedPayMethod.value?.name)?.url;
	// 點數支付
	if (isCoinPay.value) return toCoinPay(third_order_id);
	if (!payUrl) return;

	// 第三方支付
	if (import.meta.client) {
		window.location.href = payUrl;
	}
};

// 點數支付
const coin = ref(0);
getUserCoin().then(res => {
	coin.value = res.data.coin_num;
});
const isEnoughCoin = computed(() => {
	if (coin.value === "-") return false;
	return Number(coin.value) >= Number(props.finalPrice);
});
const payLoading = ref(false);
const toCoinPay = async orderId => {
	if (payLoading.value) return;
	if (!isEnoughCoin.value) {
		await navigateTo("/seer/user?type=wallet");
		showModal.value = false;
		return;
	}
	payLoading.value = true;
	await coinPay({
		order_id: orderId,
	}).catch(() => {
		payLoading.value = false;
	});
	checkPayData(orderId);
};

// 查詢是否支付完成

const checkLoading = ref(false);
const checkPayData = async orderId => {
	if (checkLoading.value) return;
	checkLoading.value = true;
	const isPay = await checkOrderStatus(orderId, 5, 3000).finally(() => {
		payLoading.value = false;
		checkLoading.value = false;
	});
	if (isPay) {
		message.success(t("user.pc.wallet.paymentSuccess"));
		props.onSuccess(successData.value);

		// 第三方像素支付事件回传
		reportPurchase(orderId, props.finalPrice);

		props.close();
	} else {
		message.error(t("user.pc.wallet.paymentNotFound"));
	}
};

// 查單
const successData = ref({});
async function checkOrderStatus(orderId, retry = 10, ms = 3000) {
	if (retry < 0) return false;

	if (typeof retry !== "number") {
		retry = 10;
	}

	const { data } = await checkPay({
		order_ids: orderId,
	});

	if (!Array.isArray(data.list) || !data.list.length || data.list[0].status === 0) {
		return new Promise(resolve => {
			setTimeout(() => {
				resolve(checkOrderStatus(orderId, retry - 1, ms));
			}, ms);
		});
	} else {
		successData.value = data.list[0];
		return data.list[0].status;
	}
}

// 支付按鈕文案
const btnText = computed(() => {
	let text = t("user.pc.wallet.payNow");
	if (isCoinPay.value && !isEnoughCoin.value) {
		text = t("user.pc.wallet.balanceInsufficient");
	}
	return payLoading.value ? t("user.pc.wallet.paymentProcessing") : text;
});
</script>

<style lang="scss" scoped>
.pc-pay-card {
	width: 1000px;
	max-width: calc(100vw - 48px);
	box-sizing: border-box;
	overflow: hidden;
	background-color: #ffffff;
	border-radius: 20px;
	:deep(.n-card__content) {
		padding: 0 !important;
	}
	.pay-title {
		font-weight: 700;
		font-size: 20px;
		color: #333333;
		padding: 24px 24px 0;
	}

	.tab {
		margin: 16px 24px 0;
		display: flex;
		flex-wrap: wrap;
		box-sizing: border-box;

		.tab-item {
			height: 44px;
			margin-right: 12px;
			margin-bottom: 12px;
			padding: 0 16px;
			font-weight: 400;
			font-size: 16px;
			color: #999999;
			white-space: nowrap;
			line-height: 42px;
			border-radius: 10px;
			background-color: #ffffff;
			border: 1px solid #efefef;
			cursor: pointer;
		}

		.active {
			line-height: 42px;
			color: #ffffff;
			border-color: #111111;
			background: #111111;
			font-weight: 700;
		}
	}

	.pay-list {
		display: flex;
		flex-wrap: wrap;
		gap: 16px 24px;
		margin: 16px 24px 32px;

		li {
			.pay-item {
				min-width: 140px;
				width: fit-content;
				max-width: 100%;
				height: 56px;
				padding: 6px 10px;
				box-sizing: border-box;
				background: #ffffff;
				border-radius: 4px;
				font-weight: 400;
				font-size: 16px;
				color: #111111;
				display: flex;
				align-items: center;
				justify-content: center;
				cursor: pointer;
				border: 0.5px solid #e5e5e5;

				.icon {
					width: 32px;
					height: 32px;
					margin-right: 8px;
				}

				.coin {
					white-space: nowrap;
					font-size: 12px;
					color: #de2828;
				}
			}
			.active {
				background: #111111;
				color: #ffffff;
			}
		}
	}
	.btn-pay {
		display: flex;
		align-items: center;
		justify-content: space-between;
		background-color: #ffffff;
		padding: 16px 24px 24px;
		border-top: 1px solid #f2f2f2;
		:deep(.n-button) {
			margin-left: auto;
			width: 364px;
			height: 56px;
			border-radius: 10px;
			font-size: 20px;
			font-weight: 700;
			background: #111111 !important;
		}
	}

	.gray {
		filter: grayscale(100%);
	}

	&__method-text {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 4px;
		font-weight: 400;
		font-size: 16px;
		line-height: 14px;
		color: #111111;
		text-align: left;
	}

	&__method-balance {
		font-size: 12px;
		line-height: 12px;
		color: #de2828;
	}

	&__footer {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&__submit {
		width: 364px;
		height: 56px;
		border-radius: 10px;
		font-weight: 700;
		font-size: 20px;
		line-height: normal;
		flex-shrink: 0;
	}

	&--en {
		font-family: Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

		.pc-pay-card__title,
		.pc-pay-card__section-title {
			font-size: 18px;
			line-height: 24px;
		}

		.pc-pay-card__area-btn {
			height: 44px;
			font-size: 15px;
		}

		.pc-pay-card__method-btn {
			width: 160px;
		}

		.pc-pay-card__method-text {
			font-size: 14px;
			line-height: 18px;
		}

		.pc-pay-card__submit {
			font-size: 18px;
		}
	}
}
</style>
